import {
    InfiniteData,
    QueryKey,
    useInfiniteQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../redux/hook'
import { updateAlertStatus } from '../../../../services/alertServices'
import { fetchAlerts } from '../../../../services/entityServices'
import { Alert, AlertsApiResponse, AlertStatusEntry } from './interfaces'
import { matchesAlert } from './utils'

export type UseAlertsDataResult = {
    queryKey: QueryKey
    flatData: Alert[]
    totalDBRowCount: number
    fetchNextPage: ReturnType<
        typeof useInfiniteQuery<AlertsApiResponse>
    >['fetchNextPage']
    isError: boolean
    isFetching: boolean
    isLoading: boolean
    updateStatus: (alert: Alert, newStatus: number) => Promise<void>
}

/**
 * Fetches an entity's alerts (paginated) and provides the status-update
 * mutation, including the optimistic cache update and the Notification
 * Center bell-count invalidation (UBQ-5164).
 */
export const useAlertsData = (
    orgId: string,
    entityId: string,
): UseAlertsDataResult => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const guestUserPreferredUnit = useAppSelector(
        (state) => state.entityInstanceView.guestUserPreferredUnit,
    )
    const user = useAppSelector((state) => state.user.user)

    const queryKey: QueryKey = useMemo(
        () => ['alerts-table-data', orgId, entityId, guestUserPreferredUnit],
        [orgId, entityId, guestUserPreferredUnit],
    )

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<AlertsApiResponse>({
            queryKey,
            queryFn: async ({ pageParam }) => {
                return fetchAlerts(
                    {
                        orgId,
                        entityId,
                        pageNumber: (pageParam as number) + 1,
                        pageSize: 20,
                    },
                    isGuestUser,
                    guestUserPreferredUnit,
                )
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                if (!lastPage?.alerts?.length) return undefined
                const totalFetched = pages.reduce(
                    (acc, page) => acc + (page?.alerts?.length || 0),
                    0,
                )
                if (totalFetched >= (lastPage?.totalRowCount || 0))
                    return undefined
                return pages.length
            },
            refetchOnWindowFocus: false,
        })

    const updateStatus = useCallback(
        async (alert: Alert, newStatus: number) => {
            if (!orgId) return

            // Optimistic update
            queryClient.setQueryData<InfiniteData<AlertsApiResponse>>(
                queryKey,
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            alerts: page.alerts.map((a: Alert) => {
                                if (!matchesAlert(a, alert)) return a

                                const newHistoryEntry: AlertStatusEntry = {
                                    id: `temp-${Date.now()}`,
                                    status: newStatus,
                                    userId: user?.id || '',
                                    userEmail: user?.email || '',
                                    createdAt: new Date().toISOString(),
                                }

                                return {
                                    ...a,
                                    status: newStatus,
                                    statusHistory: [
                                        newHistoryEntry,
                                        ...(a.statusHistory || []),
                                    ],
                                }
                            }),
                        })),
                    }
                },
            )

            const payload = {
                entityId: alert.entityId,
                ruleId: alert.ruleId,
                id: alert.id,
                timestamp: alert.timestamp,
                status: newStatus,
            }

            try {
                await updateAlertStatus(orgId, payload)
                enqueueSnackbar(t('Status updated successfully'), {
                    variant: 'success',
                })
                // Refresh the Notification Center bell badge count
                queryClient.invalidateQueries({
                    queryKey: ['alerts-unread-count-bell'],
                })
            } catch (err) {
                console.error(err)
                enqueueSnackbar(t('Failed to update status'), {
                    variant: 'error',
                })
                // Revert on failure
                queryClient.invalidateQueries({ queryKey })
            }
        },
        [orgId, queryClient, queryKey, t, user?.id, user?.email],
    )

    const flatData = useMemo(
        () => data?.pages?.flatMap((page) => page?.alerts) ?? [],
        [data],
    )
    const totalDBRowCount = data?.pages?.[0]?.totalRowCount ?? 0

    return {
        queryKey,
        flatData,
        totalDBRowCount,
        fetchNextPage,
        isError,
        isFetching,
        isLoading,
        updateStatus,
    }
}
